'use client';

import React, { useState, useRef } from 'react';
import { Check, CreditCard } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  BILLING_PLAN_TIERS,
  type BillingPlanTierDisplay,
  type BillingPlanTierId,
} from '@/constants/billingPlanTiers';
import { attachPlan } from '@/apis/attachPlan';
import headerObject from '@/types/headerObj';
import { useToast } from '@/hooks/useToast';

function formatUsdMonthly(n: number): string {
  if (n <= 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function PlanFeatureRow({ label }: { label: string }) {
  return (
    <li className="flex gap-2 items-start">
      <span className="mt-0.5 shrink-0 text-ds-brand-subtle" aria-hidden>
        <Check className="size-4" strokeWidth={2.25} />
      </span>
      <span className="ds-text-body-sm-medium text-ds-text-default text-pretty">
        {label}
      </span>
    </li>
  );
}

export interface BillingTierPlansSectionProps {
  organizationUUID: string;
  headerObj: headerObject;
  isAdmin: boolean;
  currentPlanTier: string;
  subscriptionStatus: string;
  savedCardBrand?: string;
  savedCardLast4?: string;
  onPlansChanged: () => Promise<void>;
}

export function BillingTierPlansSection({
  organizationUUID,
  headerObj,
  isAdmin,
  currentPlanTier,
  subscriptionStatus,
  savedCardBrand = '',
  savedCardLast4 = '',
  onPlansChanged,
}: BillingTierPlansSectionProps) {
  const toast = useToast();
  const [attachingId, setAttachingId] = useState<BillingPlanTierId | null>(
    null
  );
  const [selectedId, setSelectedId] = useState<BillingPlanTierId | null>(null);
  const [pendingTier, setPendingTier] = useState<BillingPlanTierDisplay | null>(
    null
  );
  const [confirming, setConfirming] = useState(false);
  const requestIdRef = useRef(0);

  const normalizedCurrent = (currentPlanTier || '').toLowerCase();
  const hasSavedCard = Boolean(savedCardLast4);

  const handleSelectTier = (tier: BillingPlanTierDisplay) => {
    if (!isAdmin || !organizationUUID) return;
    if (tier.id === 'free' || tier.contactSales) return;
    if (normalizedCurrent === tier.id && subscriptionStatus === 'active')
      return;
    setSelectedId(tier.id);
    setPendingTier(tier);
  };

  const handleConfirm = async () => {
    if (!pendingTier) return;
    const tier = pendingTier;
    const myRequestId = ++requestIdRef.current;
    setConfirming(true);
    setAttachingId(tier.id);
    let redirecting = false;

    try {
      const result = await attachPlan(organizationUUID, tier.id, headerObj);
      if (requestIdRef.current !== myRequestId) return;

      if (!result) {
        toast({
          title: 'Something went wrong',
          description: 'No response from billing. Please try again.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }
      if (result.Error) {
        toast({
          title: 'Plan update failed',
          description: result.Error,
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
        return;
      }
      if (result.PaymentUrl) {
        if (!result.PaymentUrl.startsWith('https://')) {
          toast({
            title: 'Payment redirect failed',
            description: 'Unexpected payment URL. Please contact support.',
            status: 'error',
            duration: 7000,
            isClosable: true,
          });
          return;
        }
        redirecting = true;
        setPendingTier(null);
        window.location.href = result.PaymentUrl;
        return;
      }

      // Direct charge succeeded
      setPendingTier(null);
      setSelectedId(null);
      toast({
        title: `Subscribed to ${tier.name}`,
        description: 'Your plan is now active.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await onPlansChanged();
    } catch (e) {
      if (requestIdRef.current !== myRequestId) return;
      toast({
        title: 'Plan update failed',
        description:
          e instanceof Error ? e.message : 'Unexpected error. Try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      if (!redirecting && requestIdRef.current === myRequestId) {
        setConfirming(false);
        setAttachingId(null);
      }
    }
  };

  return (
    <div className="mt-8">
      <div className="mb-6">
        <h2 className="ds-text-title-sm-semibold text-ds-text-darker text-balance">
          Plans &amp; pricing
        </h2>
        <p className="mt-2 ds-text-body-sm-medium text-ds-text-subtle max-w-3xl text-pretty">
          Platform fee by engineering headcount plus included LLM tokens. Usage
          beyond your allowance is metered (
          <span className="tabular-nums text-ds-text-default">$5</span> per 1M
          tokens). All prices are billed monthly.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {BILLING_PLAN_TIERS.map((tier) => {
          const isCurrent =
            normalizedCurrent === tier.id && subscriptionStatus === 'active';
          const isBusy = attachingId === tier.id;
          const isSelected = selectedId === tier.id;
          const isSelectable =
            tier.id !== 'free' && !tier.contactSales && isAdmin && !isCurrent;

          const cardClass = cn(
            'flex flex-col rounded-lg border p-6 transition-colors duration-150',
            isBusy || isSelected
              ? 'border-ds-brand-default bg-ds-brand-lightest ring-2 ring-ds-brand-subtle'
              : 'border-ds-border-subtlest bg-ds-surface-white',
            isSelectable && 'cursor-pointer hover:border-ds-brand-subtle'
          );

          return (
            <div
              key={tier.id}
              className={cardClass}
              onClick={isSelectable ? () => setSelectedId(tier.id) : undefined}
            >
              <div className="flex flex-col gap-1 min-h-[52px]">
                <div className="flex items-start justify-between gap-2">
                  <span className="ds-text-body-md-semibold text-ds-text-darker">
                    {tier.name}
                  </span>
                </div>
                <p className="ds-text-body-xs-medium text-ds-text-subtle">
                  {tier.subtitle}
                </p>
              </div>

              <div className="mt-5 flex flex-col gap-1">
                {tier.contactSales ? (
                  <span className="ds-text-title-md-semibold text-ds-text-darker">
                    Custom pricing
                  </span>
                ) : (
                  <div className="flex items-baseline gap-1 flex-wrap">
                    <span className="ds-text-title-md-semibold text-ds-text-darker tabular-nums">
                      {formatUsdMonthly(tier.priceMonthlyUsd)}
                    </span>
                    {tier.priceMonthlyUsd > 0 ? (
                      <span className="ds-text-body-xs-medium text-ds-text-subtle">
                        / mo
                      </span>
                    ) : null}
                  </div>
                )}
                <span className="ds-text-body-xs-medium text-ds-text-subtle">
                  {tier.contactSales
                    ? 'Contact us for a quote'
                    : tier.priceDescriptor}
                </span>
              </div>

              <div className="mt-4 pt-4 border-t border-ds-border-subtlest space-y-2">
                <p className="ds-text-body-xs-semibold text-ds-text-darker">
                  {tier.headcountLabel}
                </p>
                <p className="ds-text-body-sm-semibold text-ds-text-default tabular-nums">
                  {tier.tokensSummary}{' '}
                  <span className="ds-text-body-xs-medium text-ds-text-subtle">
                    {tier.tokensDetail}
                  </span>
                </p>
              </div>

              <ul className="mt-4 flex flex-col gap-2 flex-1">
                {tier.features.map((f) => (
                  <PlanFeatureRow key={f} label={f} />
                ))}
              </ul>

              <div className="mt-6">
                {tier.id === 'free' ? (
                  <p className="ds-text-body-xs-medium text-ds-text-subtle">
                    Included with every account
                  </p>
                ) : tier.contactSales ? (
                  <a
                    href="mailto:team@entelligence.ai"
                    className={cn(
                      buttonVariants({ variant: 'outline', size: 'sm' }),
                      'w-full'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Contact team
                  </a>
                ) : !isAdmin ? (
                  <p className="ds-text-body-xs-medium text-ds-text-subtle">
                    Only Owners can change plans.
                  </p>
                ) : isCurrent ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    disabled
                    onClick={(e) => e.stopPropagation()}
                  >
                    Current plan
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    disabled={isBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectTier(tier);
                    }}
                  >
                    {isBusy ? 'Working…' : 'Select plan'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation dialog */}
      <Dialog
        open={pendingTier !== null}
        onOpenChange={(open) => {
          if (!open && !confirming) {
            setPendingTier(null);
            setSelectedId(null);
          }
        }}
      >
        {pendingTier && (
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="ds-text-title-sm-semibold text-ds-text-darker">
                Subscribe to {pendingTier.name}
              </DialogTitle>
            </DialogHeader>

            <div className="mt-2 space-y-4">
              {/* Price */}
              <div>
                <span className="ds-text-title-md-semibold text-ds-text-darker tabular-nums">
                  {formatUsdMonthly(pendingTier.priceMonthlyUsd)}
                </span>
                <span className="ds-text-body-sm-medium text-ds-text-subtle ml-1">
                  / month
                </span>
                <p className="mt-0.5 ds-text-body-xs-medium text-ds-text-subtle">
                  {pendingTier.priceDescriptor} · Billed monthly · Cancel
                  anytime
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-1.5">
                {pendingTier.features.map((f) => (
                  <PlanFeatureRow key={f} label={f} />
                ))}
              </ul>

              {/* Payment method */}
              <div className="rounded-lg border border-ds-border-subtlest bg-ds-gray-subtlest p-3 flex items-center gap-3">
                <CreditCard className="size-4 shrink-0 text-ds-text-subtle" />
                {hasSavedCard ? (
                  <div>
                    <p className="ds-text-body-sm-medium text-ds-text-default">
                      {capitalize(savedCardBrand)} •••• {savedCardLast4}
                    </p>
                    <p className="ds-text-body-xs-medium text-ds-text-subtle">
                      Default payment method
                    </p>
                  </div>
                ) : (
                  <p className="ds-text-body-sm-medium text-ds-text-default">
                    You&apos;ll enter payment details on the next page
                  </p>
                )}
              </div>

              {/* Legal */}
              <p className="ds-text-body-xs-medium text-ds-text-subtle">
                By subscribing, you authorize Entelligence AI to charge{' '}
                {formatUsdMonthly(pendingTier.priceMonthlyUsd)}/month to your
                payment method on file until you cancel.
              </p>
            </div>

            <DialogFooter className="mt-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={confirming}
                onClick={() => {
                  setPendingTier(null);
                  setSelectedId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={confirming}
                onClick={() => void handleConfirm()}
              >
                {confirming
                  ? 'Processing…'
                  : hasSavedCard
                    ? 'Confirm & Subscribe'
                    : 'Continue to Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
